import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export enum ParamNamespace {
  'EmployeeManagement' = 'employee-management',
  'FacilityDashboard' = 'facility-dashboard',
}

type PageParams = Record<string, string>;

type ParamStore = {
  pages: Partial<Record<ParamNamespace, PageParams>>;
  setValue: (namespace: ParamNamespace, key: string, value: string) => void;
  getValue: (namespace: ParamNamespace, key: string) => string | null;
  clearPage: (namespace: ParamNamespace, keys: string[]) => void;
  clearAllPages: () => void;
};

export const useParamStore = create<ParamStore>()(
  persist(
    (set, get) => ({
      pages: {},
      setValue: (namespace, key, value) =>
        set((state) => ({
          pages: {
            ...state.pages,
            [namespace]: {
              ...(state.pages[namespace] || {}),
              [key]: value,
            },
          },
        })),
      getValue: (namespace, key): string | null =>
        get().pages[namespace]?.[key] ?? null,
      clearPage: (namespace, keys) =>
        set((state) => {
          const pageData = { ...(state.pages[namespace] || {}) };
          for (const key of keys) delete pageData[key];
          return { pages: { ...state.pages, [namespace]: pageData } };
        }),
      clearAllPages: () => set({ pages: {} }),
    }),
    { name: 'param-store-v2' }
  )
);

export type ParamConfig<TGet, TSet = TGet> = {
  default: TGet;
  parse: (value: string | null) => TGet;
  serialize: (value: TSet) => string;
  persist?: boolean;
};
export const numberParam = <T extends boolean = false>(config: {
  default: T extends true ? number | null : number;
  supportNull?: T;
  persist?: boolean;
}): ParamConfig<T extends true ? number | null : number> => ({
  default: config.default,
  parse: (v) => {
    if (v === null || (config.supportNull && v === '')) {
      return (config.supportNull ? null : config.default) as T extends true
        ? number | null
        : number;
    }
    return Number(v) as T extends true ? number | null : number;
  },
  serialize: (v) => (v !== null ? String(v) : ''),
  persist: config.persist,
});

export const stringParam = (config: {
  default: string;
  persist?: boolean;
}): ParamConfig<string> => ({
  default: config.default,
  parse: (v) => (v !== null ? v : config.default),
  serialize: (v) => v,
  persist: config.persist,
});

export const booleanParam = (config: {
  default: boolean;
  persist?: boolean;
}): ParamConfig<boolean> => ({
  default: config.default,
  parse: (v) => (v !== null ? v === 'true' : config.default),
  serialize: (v) => (v ? 'true' : 'false'),
  persist: config.persist,
});

export const numberParams = (config: {
  default: number[];
  persist?: boolean;
}): ParamConfig<number[]> => ({
  default: config.default,
  parse: (v) => (v ? v.split(',').map(Number) : config.default),
  serialize: (arr) => arr.join(','),
  persist: config.persist,
});

export const stringParams = (config: {
  default: string[];
  persist?: boolean;
}): ParamConfig<string[]> => ({
  default: config.default,
  parse: (v) => (v ? v.split(',') : config.default),
  serialize: (arr) => arr.join(','),
  persist: config.persist,
});

export const enumParam = <T extends string>(config: {
  allowed: readonly T[];
  default: T;
  persist?: boolean;
}): ParamConfig<T> => ({
  default: config.default,
  parse: (v) => {
    if (v !== null && config.allowed.includes(v as T)) return v as T;
    return config.default;
  },
  serialize: (v) => v,
  persist: config.persist,
});

export type DateRange = [Date, Date];

const isValidDate = (d: Date) => !isNaN(d.getTime());

const formatDate = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
};

export const dateParam = (config: {
  default: Date | null;
  persist?: boolean;
}): ParamConfig<Date | null> => ({
  default: config.default,
  parse: (v) => {
    if (v === null) return config.default;
    const d = new Date(v);
    return isValidDate(d) ? d : config.default;
  },
  serialize: (v) => (v ? formatDate(v) : ''),
  persist: config.persist,
});

export const dateStringParam = (config: {
  default: Date | null;
  persist?: boolean;
}): ParamConfig<string | null, Date> => ({
  default: config.default ? formatDate(config.default) : null,
  parse: (v) =>
    v !== null ? v : config.default ? formatDate(config.default) : null,
  serialize: (v) => formatDate(v),
  persist: config.persist,
});

export const dateRangeParam = (config: {
  default: DateRange | null;
  persist?: boolean;
}): ParamConfig<DateRange | null> => ({
  default: config.default,
  parse: (v) => {
    if (!v) return config.default;
    const [startStr, endStr] = v.split(',');
    const start = new Date(startStr);
    const end = new Date(endStr);
    if (!isValidDate(start) || !isValidDate(end)) return config.default;
    return [start, end];
  },
  serialize: (v) => (v ? `${formatDate(v[0])},${formatDate(v[1])}` : ''),
  persist: config.persist,
});

export const enumParams = <T extends string>(config: {
  allowed: readonly T[];
  default: T[];
  persist?: boolean;
}): ParamConfig<T[]> => ({
  default: config.default,
  parse: (v) =>
    v
      ? (v
          .split(',')
          .filter((item) => config.allowed.includes(item as T)) as T[])
      : config.default,
  serialize: (arr) => arr.join(','),
  persist: config.persist,
});

type InferValues<TSchema extends Record<string, ParamConfig<any, any>>> = {
  [K in keyof TSchema]: ReturnType<TSchema[K]['parse']>;
};

type InferSetValues<TSchema extends Record<string, ParamConfig<any, any>>> = {
  [K in keyof TSchema]: Parameters<TSchema[K]['serialize']>[0];
};

type ParamContextType<TSchema extends Record<string, ParamConfig<any, any>>> = {
  values: InferValues<TSchema>;
  setValues: (newValues: Partial<InferSetValues<TSchema>>) => void;
  resetValues: () => void;
  skipPersisted: boolean;
};

const ParamContext = createContext<ParamContextType<any> | null>(null);

export function useParamValues<
  TSchema extends Record<string, ParamConfig<any, any>>
>(
  schema: TSchema,
  namespace: ParamNamespace,
  skipPersisted = false
): ParamContextType<TSchema> {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getValue, setValue, clearPage } = useParamStore();

  const values = useMemo(() => {
    return Object.fromEntries(
      Object.entries(schema).map(([key, { default: def, parse, persist }]) => {
        const raw = searchParams.get(key);

        if (raw !== null) {
          return [key, parse(raw)];
        }

        if (!skipPersisted && persist) {
          const persisted = getValue(namespace, key);
          if (persisted !== null) {
            return [key, parse(persisted)];
          }
        }

        return [key, def];
      })
    ) as InferValues<TSchema>;
  }, [schema, searchParams, namespace, getValue, skipPersisted]);

  // On mount: sync persisted Zustand values into URL if not already present.
  // When skipPersisted is true (e.g. navigating from the dashboard), clear the
  // persisted store instead so stale filters don't reappear on future visits.
  useEffect(() => {
    if (skipPersisted) {
      clearPage(namespace, Object.keys(schema));
      return;
    }
    const updated = new URLSearchParams(searchParams);
    let changed = false;
    Object.entries(schema).forEach(([key, { persist }]) => {
      if (persist && updated.get(key) === null) {
        const persisted = getValue(namespace, key);
        if (persisted !== null) {
          updated.set(key, persisted);
          changed = true;
        }
      }
    });
    if (changed)
      navigate(
        { pathname: window.location.pathname, search: updated.toString() },
        { replace: true }
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync searchParams to persistence store when they change
  useEffect(() => {
    Object.entries(schema).forEach(([key, { persist }]) => {
      if (persist) {
        const raw = searchParams.get(key);
        if (raw !== null) {
          if (getValue(namespace, key) !== raw) {
            setValue(namespace, key, raw);
          }
        }
      }
    });
  }, [schema, searchParams, namespace, setValue, getValue]);

  const setValues = useCallback(
    (newValues: Partial<InferSetValues<TSchema>>) => {
      const updated = new URLSearchParams(searchParams);
      for (const [key, val] of Object.entries(newValues)) {
        const config = schema[key as keyof TSchema];
        if (!config) continue;
        const { serialize, persist } = config;
        const serialized = serialize(val as any);
        updated.set(key, serialized);
        if (persist) setValue(namespace, key, serialized);
      }
      navigate({
        pathname: window.location.pathname,
        search: updated.toString(),
      });
    },
    [schema, searchParams, namespace, setValue, navigate]
  );

  const resetValues = useCallback(() => {
    const keys = Object.keys(schema);
    const updated = new URLSearchParams(searchParams);
    for (const key of keys) updated.delete(key);
    clearPage(namespace, keys);
    navigate({
      pathname: window.location.pathname,
      search: updated.toString(),
    });
  }, [schema, searchParams, namespace, clearPage, navigate]);

  return useMemo(
    () => ({ values, setValues, resetValues, skipPersisted }),
    [values, setValues, resetValues, skipPersisted]
  );
}

export function ParamProvider<
  TSchema extends Record<string, ParamConfig<any, any>>
>({
  schema,
  namespace,
  children,
  skipPersisted = false,
}: {
  schema: TSchema;
  namespace: ParamNamespace;
  children: ReactNode;
  skipPersisted?: boolean;
}) {
  const value = useParamValues(schema, namespace, skipPersisted);
  return (
    <ParamContext.Provider value={value}>{children}</ParamContext.Provider>
  );
}

export function useParamsContext<
  TSchema extends Record<string, ParamConfig<any, any>>
>() {
  const ctx = useContext(ParamContext);
  if (!ctx)
    throw new Error('useParamsContext must be used inside ParamProvider');
  return ctx as ParamContextType<TSchema>;
}

export function useClearAllPageParams() {
  const { clearAllPages } = useParamStore();
  return clearAllPages;
}

export function useResetPageParams<
  TSchema extends Record<string, ParamConfig<any, any>>
>(schema: TSchema, namespace: ParamNamespace) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { clearPage } = useParamStore();

  return useCallback(
    (keys?: (keyof TSchema & string)[]) => {
      const keysToReset = keys ?? Object.keys(schema);
      const updated = new URLSearchParams(searchParams);
      for (const key of keysToReset) updated.delete(key);
      clearPage(namespace, keysToReset);
      setSearchParams(updated, { replace: true });
    },
    [schema, namespace, searchParams, clearPage, setSearchParams]
  );
}
